import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Phone, Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/4h4fx5d8_panneau%20logo%20SOPREL%20copie.png";

const navigation = [
  { name: 'Accueil', href: '/' },
  { name: 'Le Groupe', href: '/groupe' },
  { 
    name: 'Nos Produits', 
    href: '/produits',
    children: [
      { name: 'DAP Wide', href: '/produits/dap-wide' },
      { name: 'Dalles Alvéolaires', href: '/produits/dalles-alveolaires' },
      { name: 'Poutre Précontrainte', href: '/produits/poutres' },
      { name: 'Prédalle Béton Armé', href: '/produits/predalle-beton-arme' },
      { name: 'Prédalle Béton Précontraint', href: '/produits/predalle-beton-precontraint' },
    ]
  },
  { name: 'Nos Engagements', href: '/engagements' },
  { name: 'Réalisations', href: '/realisations' },
  { name: 'Recrutement', href: '/recrutement' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileProductsOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const isActive = (href) => location.pathname === href;
  const isHomePage = location.pathname === '/';

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage
            ? 'bg-[#0e1c3f] border-b border-[#0e1c3f]/50 shadow-lg' 
            : 'bg-transparent'
        }`}
        data-testid="main-header"
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo - smaller on mobile */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="SOPREL Groupe" 
                className="h-9 sm:h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:gap-1">
              {navigation.map((item) => (
                item.children ? (
                  <div 
                    key={item.name} 
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <Link
                      to={item.href}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${
                        isActive(item.href) ? 'text-[#0078b7]' : 'text-white hover:text-[#0078b7]'
                      }`}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {item.name}
                      <ChevronDown className="h-4 w-4" />
                    </Link>
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 w-56 bg-white rounded-sm shadow-lg border border-slate-200 py-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0078b7]"
                            data-testid={`nav-dropdown-${child.name.toLowerCase().replace(/\s/g, '-')}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      isActive(item.href) ? 'text-[#0078b7]' : 'text-white hover:text-[#0078b7]'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>

            {/* Contact CTA */}
            <div className="hidden lg:block">
              <Button 
                asChild
                className="rounded-sm transition-all bg-[#0078b7] text-white hover:bg-[#0078b7]/90"
                data-testid="cta-contact"
              >
                <Link to="/contact">Nous contacter</Link>
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden relative w-10 h-10 flex items-center justify-center"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <div className="relative w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 origin-center ${mobileMenuOpen ? 'rotate-45 translate-y-[9px]' : ''}`} />
                <span className={`block h-0.5 bg-white rounded-full transition-all duration-200 ${mobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`} />
                <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 origin-center ${mobileMenuOpen ? '-rotate-45 -translate-y-[9px]' : ''}`} />
              </div>
            </button>
          </div>
        </nav>
      </header>

      {/* Fullscreen Mobile Menu Overlay */}
      <div 
        ref={menuRef}
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          mobileMenuOpen ? 'visible' : 'invisible'
        }`}
        data-testid="mobile-menu-overlay"
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-[#0a1428] transition-opacity duration-500 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Menu content */}
        <div className={`relative h-full flex flex-col pt-20 transition-all duration-500 ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 translate-y-4'
        }`}>
          {/* Decorative elements */}
          <div className="absolute top-20 right-0 w-64 h-64 bg-[#0078b7]/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-20 left-0 w-48 h-48 bg-[#ff65c3]/5 rounded-full blur-3xl pointer-events-none" />

          {/* Navigation links */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {navigation.map((item, index) => {
              const active = isActive(item.href);
              const num = String(index + 1).padStart(2, '0');
              return (
                <div 
                  key={item.name}
                  className={`transition-all duration-500 ${
                    mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                  }`}
                  style={{ transitionDelay: mobileMenuOpen ? `${index * 60 + 100}ms` : '0ms' }}
                >
                  {!item.children ? (
                    <Link
                      to={item.href}
                      className="group flex items-center py-3 border-b border-white/[0.06]"
                      data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <span className={`font-mono text-xs w-8 transition-colors ${active ? 'text-[#ff65c3]' : 'text-white/20 group-hover:text-white/40'}`}>{num}</span>
                      {active && <div className="w-1 h-5 bg-[#0078b7] rounded-full mr-3" />}
                      <span className={`font-heading font-bold text-lg transition-colors ${active ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                        {item.name}
                      </span>
                      <ArrowUpRight className={`ml-auto h-4 w-4 transition-all ${active ? 'text-[#0078b7] opacity-100' : 'text-white/20 opacity-0 group-hover:opacity-100'}`} />
                    </Link>
                  ) : (
                    <div>
                      <button
                        onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                        className="group flex items-center py-3 border-b border-white/[0.06] w-full text-left"
                        data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <span className={`font-mono text-xs w-8 transition-colors ${active ? 'text-[#ff65c3]' : 'text-white/20 group-hover:text-white/40'}`}>{num}</span>
                        {active && <div className="w-1 h-5 bg-[#0078b7] rounded-full mr-3" />}
                        <span className={`font-heading font-bold text-lg transition-colors ${active ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                          {item.name}
                        </span>
                        <ChevronDown className={`ml-auto h-4 w-4 text-white/30 transition-transform duration-300 ${mobileProductsOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${mobileProductsOpen ? 'max-h-60' : 'max-h-0'}`}>
                        <div className="pl-8 py-2 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className="flex items-center gap-3 py-2 group"
                              data-testid={`mobile-nav-sub-${child.name.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              <div className={`w-1.5 h-1.5 rounded-full transition-colors ${isActive(child.href) ? 'bg-[#ff65c3]' : 'bg-white/20 group-hover:bg-[#0078b7]'}`} />
                              <span className={`text-sm transition-colors ${isActive(child.href) ? 'text-white font-medium' : 'text-white/50 group-hover:text-white/80'}`}>
                                {child.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom section - Contact info + CTA */}
          <div 
            className={`px-6 pb-8 pt-4 border-t border-white/[0.06] transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: mobileMenuOpen ? '550ms' : '0ms' }}
          >
            <div className="flex items-center gap-4 mb-5">
              <a href="tel:0155347920" className="flex items-center gap-2 text-white/40 hover:text-white/70 transition-colors text-xs">
                <Phone className="h-3.5 w-3.5" />
                01 55 34 79 20
              </a>
              <span className="w-px h-3 bg-white/10" />
              <a href="mailto:contact@soprel-groupe.fr" className="flex items-center gap-2 text-white/40 hover:text-white/70 transition-colors text-xs">
                <Mail className="h-3.5 w-3.5" />
                contact@soprel-groupe.fr
              </a>
            </div>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#0078b7] text-white font-heading font-bold text-sm rounded-sm hover:bg-[#0078b7]/80 transition-colors"
              data-testid="mobile-cta-contact"
            >
              Nous contacter
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#0e1c3f] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">

        {/* Mobile: logo centered, Desktop: 4 columns */}
        <div className="flex flex-col items-center text-center mb-8 lg:hidden">
          <img src={LOGO_URL} alt="SOPREL Groupe" className="h-10 w-auto mb-4" />
          <p className="text-slate-400 text-xs leading-relaxed max-w-xs">
            Spécialisé dans la pré-fabrication d'éléments structurels en béton, le groupe SOPREL met son expertise au service des acteurs du secteur du BTP.
          </p>
        </div>

        {/* Desktop grid */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div>
            <img src={LOGO_URL} alt="SOPREL Groupe" className="h-16 w-auto mb-6" />
            <p className="text-slate-400 text-sm leading-relaxed">
              Spécialisé dans la pré-fabrication d'éléments structurels en béton, le groupe SOPREL met son expertise au service des acteurs du secteur du BTP.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Navigation</h4>
            <ul className="space-y-2">
              {navigation.slice(0, 4).map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-slate-400 hover:text-[#0078b7] text-sm transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Nos Produits</h4>
            <ul className="space-y-2">
              <li><Link to="/produits/dap-wide" className="text-slate-400 hover:text-[#0078b7] text-sm transition-colors">DAP Wide</Link></li>
              <li><Link to="/produits/dalles-alveolaires" className="text-slate-400 hover:text-[#0078b7] text-sm transition-colors">Dalles Alvéolaires</Link></li>
              <li><Link to="/produits/poutres" className="text-slate-400 hover:text-[#0078b7] text-sm transition-colors">Poutre Précontrainte</Link></li>
              <li><Link to="/produits/predalle-beton-arme" className="text-slate-400 hover:text-[#0078b7] text-sm transition-colors">Prédalle Béton Armé</Link></li>
              <li><Link to="/produits/predalle-beton-precontraint" className="text-slate-400 hover:text-[#0078b7] text-sm transition-colors">Prédalle Béton Précontraint</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Contact</h4>
            <div className="text-slate-400 text-sm space-y-2">
              <p>32 rue de Cambrai</p>
              <p>75019 Paris, France</p>
              <p className="mt-4">
                <a href="tel:0155347920" className="hover:text-[#0078b7] transition-colors">01 55 34 79 20</a>
              </p>
              <p>
                <a href="mailto:contact@soprel-groupe.fr" className="hover:text-[#0078b7] transition-colors">contact@soprel-groupe.fr</a>
              </p>
            </div>
          </div>
        </div>

        {/* Mobile: 2-column nav + contact card */}
        <div className="lg:hidden">
          <div className="grid grid-cols-2 gap-6 mb-8">
            {/* Navigation */}
            <div>
              <h4 className="font-heading font-bold text-sm text-white/80 mb-3 uppercase tracking-wider">Navigation</h4>
              <ul className="space-y-2">
                {navigation.filter(n => !n.children).slice(0, 4).map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-slate-400 hover:text-[#0078b7] text-xs transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products */}
            <div>
              <h4 className="font-heading font-bold text-sm text-white/80 mb-3 uppercase tracking-wider">Produits</h4>
              <ul className="space-y-2">
                <li><Link to="/produits/dap-wide" className="text-slate-400 hover:text-[#0078b7] text-xs transition-colors">DAP Wide</Link></li>
                <li><Link to="/produits/dalles-alveolaires" className="text-slate-400 hover:text-[#0078b7] text-xs transition-colors">Dalles Alvéolaires</Link></li>
                <li><Link to="/produits/poutres" className="text-slate-400 hover:text-[#0078b7] text-xs transition-colors">Poutre Précontrainte</Link></li>
                <li><Link to="/produits/predalle-beton-arme" className="text-slate-400 hover:text-[#0078b7] text-xs transition-colors">Prédalle Béton Armé</Link></li>
                <li><Link to="/produits/predalle-beton-precontraint" className="text-slate-400 hover:text-[#0078b7] text-xs transition-colors">Prédalle Précontraint</Link></li>
              </ul>
            </div>
          </div>

          {/* Contact card */}
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-sm p-4 mb-6">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#0078b7]/15 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-3.5 w-3.5 text-[#0078b7]" />
                </div>
                <div className="text-xs text-slate-400">
                  <p>32 rue de Cambrai</p>
                  <p>75019 Paris, France</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <a href="tel:0155347920" className="flex items-center gap-2 text-xs text-slate-400 hover:text-[#0078b7] transition-colors">
                  <div className="w-8 h-8 bg-[#0078b7]/15 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="h-3.5 w-3.5 text-[#0078b7]" />
                  </div>
                  01 55 34 79 20
                </a>
                <a href="mailto:contact@soprel-groupe.fr" className="flex items-center gap-2 text-xs text-slate-400 hover:text-[#0078b7] transition-colors">
                  <div className="w-8 h-8 bg-[#0078b7]/15 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="h-3.5 w-3.5 text-[#0078b7]" />
                  </div>
                  contact@soprel-groupe.fr
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 mt-6 sm:mt-12 pt-6 sm:pt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-between sm:gap-4">
          <p className="text-slate-500 text-xs sm:text-sm text-center sm:text-left">
            © {new Date().getFullYear()} SOPREL Groupe. Tous droits réservés.
            <span className="hidden sm:inline"> Conception site : <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-[#0078b7] hover:text-[#ff65c3] transition-colors">WEBISULA</a></span>
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            <Link to="/mentions-legales" className="text-slate-500 hover:text-slate-300 text-xs sm:text-sm">Mentions légales</Link>
            <Link to="/politique-confidentialite" className="text-slate-500 hover:text-slate-300 text-xs sm:text-sm">Politique de confidentialité</Link>
          </div>
          <p className="text-slate-600 text-[10px] sm:hidden">
            Conception site : <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-[#0078b7]">WEBISULA</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

const PublicLayout = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default PublicLayout;
